/**
 * Sidebar Footer Component
 *
 * User profile, settings, and secondary nav items.
 */

import { Bell, ChevronRight, LogOut, PanelLeft } from 'lucide-react';

import { MODAL_IDS } from '../../types';
import { NavItem } from './nav-item';
import { cn } from '@/shared/lib';
import { useAuthStore } from '@/core/auth/auth.store';
import { useNavigate } from 'react-router-dom';
import { useNavigationItems } from '../../hooks/use-navigation-items';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/state/stores/ui.store';

interface SidebarFooterProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const SidebarFooter = ({ isCollapsed, onToggle }: SidebarFooterProps) => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { secondaryItems, isActive } = useNavigationItems();
  const { openModal } = useUIStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U'
    : 'U';
console.log("These are the secondary items", secondaryItems)
  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800">
      {/* Secondary Nav Items */}
      <div className="px-3 py-3 space-y-1">
        {secondaryItems.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            isActive={isActive(item.path)}
            isCollapsed={isCollapsed}
          />
        ))}

        {/* Notifications Button */}
        <button
          onClick={() => navigate('/notifications')}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors',
            isCollapsed && 'justify-center px-0'
          )}
          title={t('header.notifications')}
        >
          <Bell className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-sm font-medium">{t('header.notifications')}</span>
          )}
        </button>
      </div>

      {/* User Profile */}
      <div className="px-3 pb-3">
        <div
          className={cn(
            'flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer',
            isCollapsed && 'justify-center p-2'
          )}
          onClick={() => navigate('/profile')}
        >
          {/* Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium">
            {initials}
          </div>

          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {user?.email}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
            </>
          )}
        </div>

        {/* Logout Button */}
        {!isCollapsed && (
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 mt-1 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">{t('user.logout')}</span>
          </button>
        )}

        {/* Collapsed: Expand Button */}
        {isCollapsed && (
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center p-2 mt-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            title={t('sidebar.expand')}
            aria-label={t('sidebar.expand')}
          >
            <PanelLeft className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SidebarFooter;
